import {
  useCallback, useState,
} from 'react';
import { type ImportMethod } from '~/_shared/types/importMethod/importMethod';
import { isSpreadsheetImportErrorCode } from '~/_shared/types/responseErrors/_shared/spreadsheetImportErrorCodes.types';
import { isApiError } from '~/_shared/utils/api/apiError.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useImportData } from './useImportData';

type ImportTextDataProps = {
  clientId: number;
  importMethod: ImportMethod;
  input: string;
  mapId: number;
  spreadsheetId: number;
  userId: number;
  onSuccess: () => void;
};

export const useImportTextData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { importData } = useImportData();
  const [t] = useTranslation();

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const importTextData = useCallback(({
    clientId, userId, mapId, spreadsheetId, importMethod, input, onSuccess,
  }: ImportTextDataProps) => {
    setIsLoading(true);
    setErrorMessage('');

    importData({
      clientId,
      importMethod,
      mapId,
      source: naiveIfCsvToTsv(input),
      spreadsheetId,
      type: 'input',
      userId,
    })
      .then(() => {
        onSuccess();
      })
      .catch((e: unknown) => {
        setIsLoading(false);
        if (isApiError(e) && e.errorCode && isSpreadsheetImportErrorCode(e.errorCode)) {
          setErrorMessage(t(e.errorCode));
        }
        else {
          setErrorMessage(t('Something went wrong, please try again.'));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [importData, t]);

  return {
    isLoading,
    errorMessage,
    importTextData,
    clearErrorMessage,
  };
};

const naiveIfCsvToTsv = (input: string) => {
  // This is a naive implementation and will not cover all CSV edge cases, but should help in most cases.
  // It does not deal with things like quoted commas, newlines in quoted strings, etc.
  // the main concern is that this would require a library to parse CSV properly, and for now we don't want to intorudce a new dependency.

  const lines = input.split('\n');
  const firstLine = lines[0]?.trim();
  const firstLineContainsTab = firstLine?.includes('\t');
  if (firstLineContainsTab || !firstLine || lines.length < 2) {
    return input;
  }

  const firstLineNumberOfComas = firstLine.split(',').length - 1;
  const firstLineNumberOfSemicolons = firstLine.split(';').length - 1;

  if (firstLineNumberOfComas === 0 && firstLineNumberOfSemicolons === 0) {
    return input;
  }

  let targetDelimiter = ',';
  let targetAmountOfDelimiters = firstLineNumberOfComas;
  if (firstLineNumberOfComas < firstLineNumberOfSemicolons) {
    targetDelimiter = ';';
    targetAmountOfDelimiters = firstLineNumberOfSemicolons;
  }

  const allLinesSameAmountOfDelimiters = lines.every(line => {
    const numberOfDelimiters = line.split(targetDelimiter).length - 1;
    return numberOfDelimiters === targetAmountOfDelimiters;
  });

  if (allLinesSameAmountOfDelimiters) {
    return input.replace(new RegExp(`\\${targetDelimiter}`, 'g'), '\t');
  }

  return input;
};
